import React, { useState, useRef, useEffect } from 'react';

/**
 * Searchable member picker — replaces native <select> for member lists.
 *
 * Props:
 *   members     — array of { member_id, first_name, last_name, email?, status? }
 *   value       — currently selected member_id (string)
 *   onChange    — (member_id: string) => void
 *   placeholder — text shown when nothing is selected
 *   allowCreate — opt-in: show an "invite as a new speaker" row (default false)
 *   onCreate    — (query: string) => void, called when that row is clicked
 *
 * allowCreate/onCreate are opt-in so the pre-existing callers (ClubSpeechDetail,
 * MeetingDetail) are unaffected.
 */
export default function MemberPicker({
  members = [],
  value,
  onChange,
  placeholder = 'Select a member…',
  allowCreate = false,
  onCreate,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Deduplicate by member_id in case caller passes a list with repeats
  const unique = Array.from(new Map(members.map(m => [String(m.member_id), m])).values());

  const selectedMember = unique.find(m => String(m.member_id) === String(value));

  const q = query.trim().toLowerCase();
  // Match email too — an admin adding a speaker will often type the address.
  const filtered = q
    ? unique.filter(m =>
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      )
    : unique;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (member) => {
    onChange(String(member.member_id));
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const handleInputClick = () => {
    // Only reset + open on the initial click; don't clear query while already searching
    if (!open) {
      setQuery('');
      setOpen(true);
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  // When closed: show selected name; when open: show search query
  const displayValue = open ? query : (selectedMember
    ? `${selectedMember.first_name} ${selectedMember.last_name}`
    : '');

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger / search input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--bg-input)',
          border: `1px solid ${open ? 'var(--border-accent)' : 'var(--border-accent)'}`,
          borderRadius: '0.75rem',
          cursor: 'text',
        }}
        onClick={handleInputClick}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={selectedMember ? '' : placeholder}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '0.5rem 0.75rem',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            cursor: 'text',
            minWidth: 0,
          }}
        />
        {selectedMember && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              padding: '0 0.4rem',
              color: 'var(--text-faint)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
          >
            ×
          </button>
        )}
        <span
          style={{
            padding: '0 0.75rem 0 0.25rem',
            color: 'var(--text-faint)',
            fontSize: '0.625rem',
            pointerEvents: 'none',
            flexShrink: 0,
            transition: 'transform 0.15s',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          ▾
        </span>
      </div>

      {/* Dropdown — stopPropagation prevents clicks from bubbling to container's onClick */}
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-accent)',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            maxHeight: '13rem',
            overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {filtered.length === 0 && !allowCreate ? (
            <p style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--text-faint)' }}>
              No members found
            </p>
          ) : (
            filtered.map(m => {
              const isSelected = String(m.member_id) === String(value);
              return (
                <button
                  key={m.member_id}
                  type="button"
                  onClick={() => handleSelect(m)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
                    background: isSelected ? 'var(--accent-dim)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    gap: '0.5rem',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-input)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>
                    {m.first_name} {m.last_name}
                    {m.status === 'pending' && (
                      <span style={{
                        marginLeft: '0.5rem', fontSize: '0.6875rem', padding: '0.05rem 0.4rem',
                        borderRadius: '999px', background: 'var(--color-warning-dim)',
                        color: 'var(--color-warning)', border: '1px solid var(--color-warning-border)',
                      }}>
                        invite pending
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span style={{ fontSize: '0.75rem', flexShrink: 0 }}>✓</span>
                  )}
                </button>
              );
            })
          )}

          {/* The no-friction escape hatch: an admin who types a name that isn't
              in the club finds this row already under their cursor. */}
          {allowCreate && (
            <button
              type="button"
              onClick={() => { onCreate?.(query.trim()); setOpen(false); setQuery(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '0.625rem 1rem', fontSize: '0.875rem', fontWeight: 600,
                color: 'var(--accent)', background: 'transparent',
                border: 'none', borderTop: '1px solid var(--border-subtle)',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-dim)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              ＋ {query.trim()
                ? `Invite "${query.trim()}" as a new speaker`
                : 'Add someone not in the club'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
